import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowRight, SkipForward, Loader2, Sparkles } from "lucide-react";

const STEPS = [
  { key: "owner_name", q: "First, what's your name?", ph: "Jordan", type: "text" },
  { key: "business_name", q: "What's your school or gym called?", ph: "Apex Martial Arts", type: "text" },
  { key: "business_type", q: "What type of business is it?", ph: "BJJ / MMA / Boxing / Fitness gym", type: "text" },
  { key: "styles", q: "Which styles or programs do you run?", ph: "Kids BJJ, Adult MMA, Kickboxing", type: "text" },
  { key: "location", q: "Where are you located?", ph: "Austin, TX", type: "text" },
  { key: "website", q: "Website? (optional)", ph: "https://…", type: "text" },
  { key: "members", q: "Roughly how many active members do you have?", ph: "140", type: "number" },
  { key: "avg_price", q: "Average monthly membership price?", ph: "165", type: "number" },
  { key: "monthly_revenue", q: "Approximate monthly revenue?", ph: "23000", type: "number" },
  { key: "ad_spend", q: "How much do you spend on ads per month?", ph: "2000", type: "number" },
  { key: "leads", q: "How many leads do you get per month?", ph: "70", type: "number" },
  { key: "appts_booked", q: "How many book an appointment / intro?", ph: "40", type: "number" },
  { key: "appts_showed", q: "How many actually show up?", ph: "28", type: "number" },
  { key: "enrollments", q: "How many enroll as members each month?", ph: "12", type: "number" },
  { key: "cancellations", q: "How many members cancel each month?", ph: "6", type: "number" },
  { key: "crm", q: "What CRM or software do you use? (optional)", ph: "GoHighLevel, Kicksite, none…", type: "text" },
  { key: "primary_goal", q: "What's your #1 goal right now?", ph: "Hit $35k/mo by summer", type: "text" },
  { key: "biggest_problem", q: "What's the biggest problem holding you back?", ph: "Leads don't convert / high churn / ad costs", type: "textarea" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [val, setVal] = useState("");
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const step = STEPS[i];
  const progress = Math.round((i / STEPS.length) * 100);

  const commit = (skip = false) => {
    const next = { ...data };
    if (!skip && val.trim()) next[step.key] = step.type === "number" ? Number(val) : val.trim();
    setData(next);
    setVal("");
    if (i + 1 < STEPS.length) setI(i + 1);
    else finish(next);
  };

  const finish = async (payload) => {
    setSaving(true);
    try {
      await api.put("/profile", { data: payload });
      toast.success("Your school profile is set. Let's find your bottleneck.");
      navigate("/coach");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setSaving(false);
    }
  };

  const skipAll = async () => finish(data);

  return (
    <div className="min-h-screen bg-background mm-grid-bg flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 max-w-3xl mx-auto w-full">
        <Logo />
        <button onClick={skipAll} data-testid="skip-onboarding" className="text-sm text-slate-400 hover:text-slate-200">
          Skip for now
        </button>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 pb-20">
        <div className="w-full max-w-xl mm-fade-in" key={i}>
          <Progress value={progress} className="h-1.5 mb-8 bg-slate-800" />
          <div className="inline-flex items-center gap-2 text-emerald-400 text-xs font-mono uppercase tracking-widest mb-4">
            <Sparkles className="h-3.5 w-3.5" /> Step {i + 1} of {STEPS.length}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight">{step.q}</h1>
          <p className="mt-2 text-sm text-slate-400">Every field is optional — the more you share, the sharper your coaching.</p>

          <div className="mt-7">
            {step.type === "textarea" ? (
              <Textarea
                data-testid="onboarding-input"
                autoFocus value={val} onChange={(e) => setVal(e.target.value)} placeholder={step.ph}
                className="bg-slate-900 border-slate-700 min-h-24 text-base"
              />
            ) : (
              <Input
                data-testid="onboarding-input"
                autoFocus type={step.type} value={val} onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && commit()} placeholder={step.ph}
                className="bg-slate-900 border-slate-700 h-13 py-3 text-base"
              />
            )}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <Button data-testid="onboarding-next" onClick={() => commit()} disabled={saving} className="h-11 px-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : (<>{i + 1 === STEPS.length ? "Finish" : "Continue"} <ArrowRight className="h-4 w-4 ml-2" /></>)}
            </Button>
            <Button variant="ghost" data-testid="onboarding-skip" onClick={() => commit(true)} disabled={saving} className="text-slate-400 hover:text-slate-200">
              <SkipForward className="h-4 w-4 mr-1.5" /> Skip
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
